import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../../components/Table/IndentTable";
import Header from "../../../components/Header/Header";
import ActionBar from "../../../components/CRUDcomponents/Actionbar/ActionBar";
import Footer from "../../../components/Footer/Footer";
import axios from "axios";
import { useSelector } from "react-redux";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const apiEndpoint = `${API_BASE_URL}/shpage/indent`;

const SHIndent = () => {
  const [indents, setIndents] = useState([]);
  const [filteredIndents, setFilteredIndents] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { role } = useSelector((state) => state.auth);
  const [dropdownData, setDropdownData] = useState({
    users: [],
    projects: [],
    stages: [],
    types: [],
  });



  const getEditFields = () => [
    {
      name: "ProjectID",
      label: "Project",
      type: "select",
      options:
        dropdownData.projects.map((project) => ({
          value: project.id,
          label: project.name,
        })) || [],
    },
    {
      name: "TypeID",
      label: "Type",
      type: "select",
      options:
        dropdownData.types.map((type) => ({
          value: type.id,
          label: type.name,
        })) || [],
    },
  ];

  const navigate = useNavigate();
  const getApiEndpoint = () => {
    return role === "admin" 
      ? `${API_BASE_URL}/adminpage/shindent` 
      : `${API_BASE_URL}/shpage/indent`;
  };
  const fetchIndents = async () => {
    try {
      const apiEndpoint = getApiEndpoint();
      const response = await axios.get(apiEndpoint);
      const formattedIndents = response.data
        .map((indent) => ({
          ...indent,
          L1: indent.L1 ? "Approved" : "Pending",
          L2: indent.L2 ? "Approved" : "Pending",
          PO: indent.PO ? "Approved" : "Pending",
          PH: indent.PH ? "Approved" : "Pending",
          IR: indent.IR ? "Received" : "Pending",
          SH: indent.SH ? "Received" : "Pending",
          CreationDate: indent.CreationDate || "N/A"
        }))
        .sort((a, b) => {
          const parseDate = (dateStr) => {
            if (!dateStr || dateStr === "N/A") return new Date(0);
            const [day, month, year] = dateStr.split("/");
            return new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
          };
          return parseDate(b.CreationDate) - parseDate(a.CreationDate);
        });
      setIndents(formattedIndents);
      setFilteredIndents(formattedIndents);
    } catch (error) {
      console.error("Error fetching indents:", error);
    }
  };

  const fetchDropdownData = async () => {
    try {
      const [usersRes, projectsRes, stagesRes, typesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/dropdown/users`),
        axios.get(`${API_BASE_URL}/dropdown/mprojects`),
        axios.get(`${API_BASE_URL}/dropdown/mstages`),
        axios.get(`${API_BASE_URL}/dropdown/mindenttypes`),
      ]);

      const dropdownDataState = {
        users: usersRes.data.map((user) => ({
          id: user.UserID,
          name: user.Username,
        })),
        projects: projectsRes.data.map((project) => ({
          id: project.ProjectID,
          name: project.ProjectName,
        })),
        stages: stagesRes.data.map((stage) => ({
          id: stage.StageID,
          name: stage.StageName,
        })),
        types: typesRes.data.map((type) => ({
          id: type.TypeID,
          name: type.TypeName,
        })),
      };

      setDropdownData(dropdownDataState);
    } catch (error) {
      console.error("Error fetching dropdown data:", error);
    }
  };

  useEffect(() => {
    fetchIndents();
    fetchDropdownData();
  }, []);

  const handleSearch = (query) => {
    setSearchQuery(query);
    if (!query || query.trim() === '') {
      setFilteredIndents(indents);
      return;
    }
    
    const searchTerm = query.toLowerCase().trim();
    const filtered = indents.filter((indent) => {
      const searchableFields = [
        indent.IndentID?.toString(),
        indent.Status,
        indent.ProjectName,
        indent.TypeName,
        indent.CreationDate,
        indent.TaskSchedule,
        indent.L1?.toString(),
        indent.L2?.toString(),
        indent.PO?.toString(),
        indent.PH?.toString(),
        indent.IR?.toString(),
        indent.SH?.toString()
      ];
      
      return searchableFields.some(field => 
        field && field.toString().toLowerCase().includes(searchTerm)
      );
    });
    
    setFilteredIndents(filtered);
  };

  const handleDateFilter = (filterOptions) => {
    const { dateFrom, dateTo, indentType } = filterOptions;
    let filtered = indents;

    // Apply date filter if dates are provided
    if (dateFrom && dateTo) {
      filtered = filtered.filter((indent) => {
        if (!indent.CreationDate || indent.CreationDate === "N/A") return false;
        
        // Parse dd/mm/yy date format
        const [day, month, year] = indent.CreationDate.split('/');
        const creationDate = new Date(2000 + parseInt(year), parseInt(month) - 1, parseInt(day));
        const fromDate = new Date(dateFrom);
        const toDate = new Date(dateTo);
        
        return creationDate >= fromDate && creationDate <= toDate;
      });
    }

    // Apply indent type filter if selected
    if (indentType) {
      filtered = filtered.filter((indent) => {
        const typeName = (indent.TypeName || indent.Type || '').toLowerCase().trim();
        return typeName.includes(indentType.toLowerCase().trim());
      });
    }

    setFilteredIndents(filtered);
  };

  // Button click handlers for redirection
  const handleRedirect = (page) => {
    navigate(page);
  };

  const handleRowClick = (row) => {
    navigate(`/SHtable`, { state: { row } });
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-grow p-4">
        <div className="mt-6 mb-2 text-center">
          <h3 className="text-xl font-semibold">Store Head Page</h3>
        </div>

        {dropdownData.users.length > 0 && dropdownData.projects.length > 0 && (
          <ActionBar
            title="Indent"
            fields={getEditFields()}
            apiEndpoint={apiEndpoint}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={setSelectedRow}
            onRefresh={fetchIndents}
            onSearch={handleSearch}
            onFilter={handleDateFilter}
            rowKey="IndentID"
            userRole={role}
            editRoute="/SHtable"
            searchQuery={searchQuery}
          />
        )}

        <div className="mt-4 overflow-x-auto">
          <DynamicTable
            columns={[
              { key: "IndentID", label: "Indent ID" },
              { 
                key: "CreationDate", 
                label: "Creation Date",
                format: (value) => value || "N/A"
              },
              { key: "ProjectName", label: "Project" },
              { key: "TypeName", label: "Indent Type" },
              { key: "TaskSchedule", label: "Task Scheduled" },
              { key: "Status", label: "Status" },
              { key: "ItemCount", label: "Item Count" },
            ]}
            data={filteredIndents}
            selectedRow={selectedRow}
            onRowSelect={(row) => setSelectedRow(row)}
            onDoubleClick={(row) => handleRowClick(row)}
            rowKey="IndentID"
            userIdKey="UserID"
            highlightColor="bg-green-100"
            noDataMessage="No indents found."
            userRole={role}
          />
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default SHIndent;
